import { Formik, Form } from "formik";
import TextField from "../components/common/TextField";
import Button from "../components/common/Button";
import { ReactNode, useMemo } from "react";
import { useFetchAllCitiesQuery } from "../store";
import classNames from "classnames";
import { City, TransportType, TransportTypeLabels } from "../types";

interface RiderFormProps {
  initialValues: any;
  validationSchema: any;
  onSubmit: (
    values: any,
    formikHelpers: { resetForm: () => void }
  ) => Promise<void>;
  isLoading?: boolean;
}

const sectionTitleClasses = "mr-auto mt-4 text-primary font-semibold";

const RiderForm: React.FC<RiderFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  isLoading,
}) => {
  const { data: cities } = useFetchAllCitiesQuery();

  const availableCities = useMemo(() => {
    if (!cities) return [];
    return cities.map((city: City) => ({
      value: city._id,
      label: city.displayName,
    }));
  }, [cities]);

  const errorMessageClasses = classNames(
    "w-full text-sm text-red-700 border-b border-red-700 rounded"
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        setFieldValue,
      }) => (
        <Form className="mx-auto w-3/5 flex flex-col items-center mt-12 gap-3">
          {/* Basic Info */}
          <TextField
            id="firstName"
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Prénom"
          />

          <TextField
            id="lastName"
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Nom"
          />

          <TextField
            id="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Email"
          />

          <TextField
            id="password"
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Mot de passe"
            hideable
          />

          <TextField
            id="phone"
            value={values.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Téléphone"
          />

          {/* Address Section */}
          <h3 className={sectionTitleClasses}>Adresse</h3>

          <TextField
            id="address.street"
            value={values.address.street}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Rue"
          />

          <TextField
            id="address.zipCode"
            value={values.address.zipCode}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Code postal"
          />

          <TextField
            id="address.city"
            value={values.address.city}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            placeholder="Ville"
          />

          {/* City selection (single) */}
          <h3 className={sectionTitleClasses}>Ville</h3>
          <select
            id="city"
            value={values.city}
            onChange={(e) => setFieldValue("city", e.target.value)}
            onBlur={handleBlur}
            className="border rounded p-2 w-full"
          >
            <option
              value=""
              disabled
            >
              Sélectionner une ville
            </option>
            {availableCities.map((city: any) => (
              <option
                key={city.value}
                value={city.value}
              >
                {city.label}
              </option>
            ))}
          </select>
          {errors.city && touched.city && (
            <div className={errorMessageClasses}>
              {errors.city as ReactNode}
            </div>
          )}

          {/* Transport Type Selection */}
          <h3 className={sectionTitleClasses}>Type de transport</h3>
          <select
            id="transportType"
            value={values.transportType}
            onChange={(e) => setFieldValue("transportType", e.target.value)}
            onBlur={handleBlur}
            className="border rounded p-2 w-full"
          >
            <option
              value=""
              disabled
            >
              Sélectionner un moyen de transport
            </option>
            {Object.values(TransportType).map((type) => (
              <option
                key={type}
                value={type}
              >
                {TransportTypeLabels[type]}
              </option>
            ))}
          </select>
          {errors.transportType && touched.transportType && (
            <div className={errorMessageClasses}>
              {errors.transportType as ReactNode}
            </div>
          )}

          <Button
            className="w-1/2 justify-center font-bold py-2 bg-primary rounded text-white mt-4"
            disabled={isSubmitting || isLoading}
            type="submit"
          >
            Enregistrer
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default RiderForm;
